import { useCallback, useMemo, useReducer, useRef } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import {
  applyLearnedAutoFillMerge,
  isLearnedAutoFillMergeItemActionable,
  MAX_LEARNED_LIBRARY_ENTRIES,
  planLearnedAutoFillMerge,
  sanitizeLearnedAutoFillPatterns,
} from '../../../shared/lib/quickFill/learnedAutoFillLibrary';
import { useLearnedAutoFillLibraryStore } from '../../../shared/stores/useLearnedAutoFillLibraryStore';
import {
  buildQuickFillKeyLibraryDraftPreview,
  findBuiltInShadowedPatternIds,
  getQuickFillKeyLibraryValidationError,
  type QuickFillKeyLibraryEditableField,
} from '../lib/quickFillKeyLibraryDrafts';
import {
  createQuickFillKeyLibraryEditorState,
  getQuickFillKeyLibraryHasDiscardableWork,
  getQuickFillKeyLibraryIsDirty,
  quickFillKeyLibraryEditorReducer,
} from '../lib/quickFillKeyLibraryEditorModel';
import {
  downloadQuickFillKeyLibrary,
  readQuickFillKeyLibraryFile,
} from '../lib/quickFillKeyLibraryFileSharing';

export interface UseQuickFillKeyLibraryOptions {
  quickFillData: readonly QuickFillItem[];
  selectedRows: readonly number[];
}

export interface QuickFillKeyLibraryMergeSummary {
  newCount: number;
  unchangedCount: number;
  replacementRequiredCount: number;
  actionableCount: number;
  builtInShadowedCount: number;
}

export function useQuickFillKeyLibrary({
  quickFillData,
  selectedRows,
}: UseQuickFillKeyLibraryOptions) {
  const entries = useLearnedAutoFillLibraryStore((store) => store.entries);
  const replaceEntries = useLearnedAutoFillLibraryStore((store) => store.replaceEntries);
  const [state, dispatch] = useReducer(
    quickFillKeyLibraryEditorReducer,
    entries,
    createQuickFillKeyLibraryEditorState
  );
  const importRequestIdRef = useRef(0);

  const mergePlan = useMemo(
    () => planLearnedAutoFillMerge(state.libraryEntries, state.drafts),
    [state.drafts, state.libraryEntries]
  );
  const builtInShadowedIds = useMemo(
    () => findBuiltInShadowedPatternIds(state.drafts),
    [state.drafts]
  );
  const localBuiltInShadowedIds = useMemo(
    () => findBuiltInShadowedPatternIds(state.libraryEntries),
    [state.libraryEntries]
  );
  const libraryValidationError = useMemo(
    () => getQuickFillKeyLibraryValidationError(state.libraryEntries),
    [state.libraryEntries]
  );
  const hasUnsavedLibraryChanges = getQuickFillKeyLibraryIsDirty(state);
  const hasDiscardableWork = getQuickFillKeyLibraryHasDiscardableWork(state);

  const mergeSummary = useMemo<QuickFillKeyLibraryMergeSummary>(() => ({
    newCount: mergePlan.filter((item) => item.status === 'new').length,
    unchangedCount: mergePlan.filter((item) => item.status === 'unchanged').length,
    replacementRequiredCount: mergePlan.filter((item) => item.status === 'conflict').length,
    actionableCount: mergePlan.filter((item) =>
      isLearnedAutoFillMergeItemActionable(item, state.confirmedConflictIds)
    ).length,
    builtInShadowedCount: builtInShadowedIds.size,
  }), [builtInShadowedIds, mergePlan, state.confirmedConflictIds]);

  const openFromTable = useCallback(() => {
    importRequestIdRef.current += 1;
    if (selectedRows.length === 0) {
      dispatch({ type: 'open-manage', entries });
      return;
    }

    const preview = buildQuickFillKeyLibraryDraftPreview(quickFillData, selectedRows);
    dispatch({
      type: 'open-preview',
      entries,
      drafts: preview.drafts,
      source: 'table',
      skippedRowCount: preview.skippedRowCount,
      duplicateRowCount: preview.duplicateRowCount,
    });
  }, [entries, quickFillData, selectedRows]);

  const changeEntry = useCallback((
    target: 'change-draft-entry' | 'change-library-entry',
    index: number,
    field: QuickFillKeyLibraryEditableField,
    value: string
  ) => dispatch({ type: target, index, field, value }), []);

  const handleConfirm = useCallback(() => {
    if (mergeSummary.actionableCount === 0) {
      return;
    }

    const mergedEntries = applyLearnedAutoFillMerge(
      state.libraryEntries,
      mergePlan,
      state.confirmedConflictIds
    );
    if (mergedEntries.length > MAX_LEARNED_LIBRARY_ENTRIES) {
      dispatch({
        type: 'set-error',
        error: `The merged dictionary would exceed ${MAX_LEARNED_LIBRARY_ENTRIES.toLocaleString()} entries.`,
      });
      return;
    }

    const validationError = getQuickFillKeyLibraryValidationError(mergedEntries);
    if (validationError) {
      dispatch({ type: 'set-error', error: validationError });
      return;
    }

    const sanitizedEntries = sanitizeLearnedAutoFillPatterns(mergedEntries);
    replaceEntries(sanitizedEntries);
    dispatch({
      type: 'persist-success',
      entries: sanitizedEntries,
      notice: 'Saved locally on this workstation',
    });
  }, [mergePlan, mergeSummary.actionableCount, replaceEntries, state]);

  const handleSaveLibrary = useCallback(() => {
    if (libraryValidationError || !hasUnsavedLibraryChanges) {
      return;
    }
    const sanitizedEntries = sanitizeLearnedAutoFillPatterns(state.libraryEntries);
    replaceEntries(sanitizedEntries);
    dispatch({
      type: 'save-local-success',
      entries: sanitizedEntries,
      notice: 'Dictionary changes saved on this workstation',
    });
  }, [hasUnsavedLibraryChanges, libraryValidationError, replaceEntries, state.libraryEntries]);

  const startImport = useCallback(async (file: File) => {
    const requestId = ++importRequestIdRef.current;
    dispatch({ type: 'start-import' });
    const parsed = await readQuickFillKeyLibraryFile(file);
    if (requestId !== importRequestIdRef.current) {
      return;
    }
    dispatch(parsed.ok
      ? { type: 'import-success', entries: parsed.document.entries }
      : { type: 'import-error', error: parsed.error });
  }, []);

  const handleImportFile = useCallback((file: File): void | Promise<void> => {
    if (hasDiscardableWork) {
      dispatch({ type: 'request-import', file });
      return;
    }
    return startImport(file);
  }, [hasDiscardableWork, startImport]);

  const handleDiscardGuard = useCallback(() => {
    const intent = state.guardIntent;
    if (intent?.type === 'close') {
      dispatch({ type: 'discard-close' });
    } else if (intent?.type === 'import') {
      void startImport(intent.file);
    } else if (intent?.type === 'delete-selected') {
      dispatch({ type: 'delete-selected' });
    }
  }, [startImport, state.guardIntent]);

  const handleAddLibraryEntry = useCallback(() => {
    if (state.libraryEntries.length >= MAX_LEARNED_LIBRARY_ENTRIES) {
      dispatch({
        type: 'set-error',
        error: `A dictionary can contain at most ${MAX_LEARNED_LIBRARY_ENTRIES.toLocaleString()} entries.`,
      });
      return;
    }
    dispatch({ type: 'add-entry' });
  }, [state.libraryEntries.length]);

  const handleExport = useCallback(() => {
    if (!hasUnsavedLibraryChanges) {
      downloadQuickFillKeyLibrary(entries);
    }
  }, [entries, hasUnsavedLibraryChanges]);

  const handleClose = useCallback(() => {
    importRequestIdRef.current += 1;
    dispatch({ type: 'request-close' });
  }, []);

  return {
    entryCount: entries.length,
    openFromTable,
    dialogProps: {
      open: state.open,
      source: state.source,
      libraryEntryCount: entries.length,
      libraryEntries: state.libraryEntries,
      mergePlan,
      mergeSummary,
      skippedRowCount: state.skippedRowCount,
      duplicateRowCount: state.duplicateRowCount,
      builtInShadowedIds,
      localBuiltInShadowedIds,
      confirmedConflictIds: state.confirmedConflictIds,
      selectedLocalEntryIndexes: state.selectedLocalEntryIndexes,
      error: state.error,
      notice: state.notice,
      libraryValidationError,
      hasUnsavedLibraryChanges,
      canAddLibraryEntry: state.libraryEntries.length < MAX_LEARNED_LIBRARY_ENTRIES,
      guardIntent: state.guardIntent?.type,
      onClose: handleClose,
      onCancelGuard: () => dispatch({ type: 'cancel-guard' }),
      onDiscardGuard: handleDiscardGuard,
      onDraftEntryChange: (
        index: number, field: QuickFillKeyLibraryEditableField, value: string
      ) => changeEntry('change-draft-entry', index, field, value),
      onLibraryEntryChange: (
        index: number, field: QuickFillKeyLibraryEditableField, value: string
      ) => changeEntry('change-library-entry', index, field, value),
      onDeleteLibraryEntry: (index: number) => dispatch({ type: 'delete-entry', index }),
      onAddLibraryEntry: handleAddLibraryEntry,
      onRevertLibrary: () => dispatch({ type: 'revert-local-edits' }),
      onToggleLocalEntrySelection: (index: number, selected: boolean) =>
        dispatch({ type: 'toggle-selection', index, selected }),
      onToggleVisibleLocalEntries: (indexes: readonly number[], selected: boolean) =>
        dispatch({ type: 'set-visible-selection', indexes, selected }),
      onRequestBulkDelete: () => dispatch({ type: 'request-delete-selected' }),
      onSaveLibrary: handleSaveLibrary,
      onConfirmConflict: (id: string, confirmed: boolean) =>
        dispatch({ type: 'confirm-conflict', id, confirmed }),
      onConfirm: handleConfirm,
      onExport: handleExport,
      onImportFile: handleImportFile,
    },
  };
}
